import './TagCreationForm.scss';
import * as Dfns from 'date-fns/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { DATE_FORMAT, TODAY } from '@blisspointmedia/bpm-types/dist/RelativeDatePicker';
import { Button } from '@Components/Button';
import { getPlacementTypeDisabledReason, PlacementSummary } from '@Components/CreativeAllocation';
import { DatePicker } from '@Components/DatePicker';
import { Placement, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { useExperimentFlag } from '../../../utils/experiment-context';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { DeviceNames, hasDeviceTags } from '../utils';
import { DimensionsPicker } from './DimensionsPicker';
import { DIMENSION_OPTIONS } from './generate-view-constants';
import { AdServer, TagData } from './utils';

interface TagCreationFormProps {
  tag: TagData;
  setTag: (tag: TagData) => void;
  onSubmit: (updatedTag: TagData) => void;
  flashtalkingCampaigns?: { campaign_id: number; campaign: string; is_managed_service: boolean }[];
  additionalSourceTagCount?: number;
}

interface TagFormData {
  adServer: AdServer;
  deviceMap?: Record<string, boolean>;
  lengthMap?: Record<string, boolean>;
  additionalParams?: string[];
  vendor?: string;
  campaignID?: number;
  startDate?: string;
  endDate?: string;
  placementType?: '' | 'Audio' | 'Pixel' | 'VAST' | 'Display';
  transcodeVendor?: string;
  extraTrackingPixels?: string[];
  dimensions?: string;
  dimensionsMap?: Record<string, boolean>;
}

export const FLASHTALKING_DSP_TO_VENDOR_MAP: Record<string, string> = {
  // "Roku OneView": "Roku",
  // Amobee: "Amobee",
  // YouTube: "YouTube", TODO: Add this back when we have certified YouTube
  Amazon: 'Amazon',
  DV360: 'DV360',
  Nexxen: 'Nexxen',
  SAM: 'Spotify',
  TradeDesk: 'The Trade Desk',
  Walmart: 'Walmart Connect',
  Xandr: 'Microsoft Invest',
  Yahoo: 'Yahoo'
};

// Maps a selected placementType value back to the key used in a vendor's
// `placement_types` list, so vendor options can be filtered by placement type.
export const PLACEMENT_TYPE_TO_VENDOR_KEY: Record<string, string> = {
  Audio: 'audio_vast',
  Pixel: 'pixel',
  VAST: 'video_vast',
  Display: 'display'
};

const INSTACART_PIXEL_OPTIONS = [
  { label: 'None', value: '' },
  {
    label: 'Evergreen OTT',
    value:
      'https://pi.ispot.tv/v2/TC-3848-1.gif?platform=ott&publisher=[%siteName%]&placementid=[%placementID%]&creativename=[%creativeName%]&creativeid=[%creativeID%]&campaignid=[%campaignID%]&customdata=[%placementName%]&dsp=flashtalking&supplierid=[%siteID%]'
  },
  {
    label: 'Remar OTT',
    value:
      'https://pi.ispot.tv/v2/TC-3848-1.gif?platform=ott&publisher=[%siteName%]&placementid=[%placementID%]&creativename=[%creativeName%]&creativeid=[%creativeID%]&campaignid=REMAR&customdata=[%placementName%]&dsp=flashtalking&supplierid=[%siteID%]'
  },
  {
    label: 'Strategic OTT',
    value:
      'https://pi.ispot.tv/v2/TC-3848-1.gif?platform=ott&publisher=[%siteName%]&placementid=[%placementID%]&creativename=[%creativeName%]&creativeid=[%creativeID%]&campaignid=STRAT&customdata=[%placementName%]&dsp=flashtalking&supplierid=[%siteID%]'
  },
  {
    label: 'Experimental OTT',
    value:
      'https://pi.ispot.tv/v2/TC-3848-1.gif?platform=ott&publisher=[%siteName%]&placementid=[%placementID%]&creativename=[%creativeName%]&creativeid=[%creativeID%]&campaignid=EXP&customdata=[%placementName%]&dsp=flashtalking&supplierid=[%siteID%]'
  },
  {
    label: 'Evergreen OLV',
    value:
      'https://pi.ispot.tv/v2/TC-3848-1.gif?platform=digital&publisher=[%siteName%]&placementid=[%placementID%]&creativename=[%creativeName%]&creativeid=[%creativeID%]&campaignid=REMAR&customdata=[%placementName%]&dsp=flashtalking&supplierid=[%siteID%]'
  },
  {
    label: 'Remar OLV',
    value:
      'https://pi.ispot.tv/v2/TC-3848-1.gif?platform=digital&publisher=[%siteName%]&placementid=[%placementID%]&creativename=[%creativeName%]&creativeid=[%creativeID%]&campaignid=STRAT&customdata=[%placementName%]&dsp=flashtalking&supplierid=[%siteID%]'
  },
  {
    label: 'Strategic OLV',
    value:
      'https://pi.ispot.tv/v2/TC-3848-1.gif?platform=digital&publisher=[%siteName%]&placementid=[%placementID%]&creativename=[%creativeName%]&creativeid=[%creativeID%]&campaignid=EXP&customdata=[%placementName%]&dsp=flashtalking&supplierid=[%siteID%]'
  },
  {
    label: 'Experimental OLV',
    value:
      'https://pi.ispot.tv/v2/TC-3848-1.gif?platform=digital&publisher=[%siteName%]&placementid=[%placementID%]&creativename=[%creativeName%]&creativeid=[%creativeID%]&campaignid=[%campaignID%]&customdata=[%placementName%]&dsp=flashtalking&supplierid=[%siteID%]'
  }
];

export const TagCreationForm = ({
  tag,
  setTag,
  onSubmit,
  flashtalkingCampaigns = [],
  additionalSourceTagCount = 0
}: TagCreationFormProps) => {
  const companyInfo = useCompanyInfo();
  const [instacartPixel, setInstacartPixel] = useState('');
  // State initializes from the tag prop so the form is repopulated when the user
  // navigates Back to it from a later step (the tag carries prior selections).
  const [deviceMap, setDeviceMap] = useState(
    tag.deviceMap ?? {
      [DeviceNames.MOBILE]: true,
      [DeviceNames.CTV]: true,
      [DeviceNames.DESKTOP]: true
    }
  );
  const hasValidDevices = Object.values(deviceMap).includes(true);

  const [lengthMap, setLengthMap] = useState(
    tag.lengthMap ?? {
      '15': false,
      '30': false,
      '60': false,
      '120': false
    }
  );
  const hasValidLengths = Object.values(lengthMap).includes(true);

  const allowAdditionalTagParams = useExperimentFlag('allowAdditionalTagParams');
  const [additionalParams, setAdditionalParams] = useState<string[]>(tag.additionalParams ?? []);

  const splitAdditionalParams = (input: string) => {
    setAdditionalParams(input.split(',').map(param => param.trim()));
  };

  const [adServer, setAdServer] = useState<AdServer>(tag.adServer ?? 'Flashtalking');
  const [placementType, setPlacementType] = useState<'' | 'Audio' | 'Pixel' | 'VAST' | 'Display'>(
    tag.placementType ?? ''
  );

  // Vendor options are driven by the selected placement type: a vendor only
  // appears if it supports the chosen placement type (DEV-22505). Before a
  // placement type is picked, all vendors are shown.
  const vendorOptions = useMemo(() => {
    const ftVendors = tag.flashtalking_vendors ?? [];
    const placementTypeKey = PLACEMENT_TYPE_TO_VENDOR_KEY[placementType];
    const compatibleVendors = placementTypeKey
      ? ftVendors.filter(v => v.placement_types.includes(placementTypeKey))
      : ftVendors;
    const tagFlashtalkingVendors = [...new Set(compatibleVendors.map(v => v.name))];
    if (tag.dsp && adServer === 'Flashtalking' && FLASHTALKING_DSP_TO_VENDOR_MAP[tag.dsp] == null) {
      return [];
    }
    if (tag.dsp && tag.network !== 'SPOTIFY' && adServer === 'Flashtalking') {
      return [...new Set([FLASHTALKING_DSP_TO_VENDOR_MAP[tag.dsp], ...tagFlashtalkingVendors])];
    }
    if (adServer === 'Flashtalking') {
      return tagFlashtalkingVendors;
    }
    return [...new Set(tag.vendorOptions ?? [])];
  }, [adServer, placementType, tag]);

  const hasOnlyOneVendor = (vendorOptions || []).length === 1;
  const [vendor, setVendor] = useState<string>(
    tag.vendor ?? (hasOnlyOneVendor ? vendorOptions[0] : '')
  );
  const [transcodeVendor, setTranscodeVendor] = useState<string>(tag.transcodeVendor ?? '');
  const [campaignID, setCampaignID] = useState<number | undefined>(tag.campaignID);
  const [startDate, setStartDate] = useState<string | undefined>(tag.startDate);
  const [endDate, setEndDate] = useState<string | undefined>(tag.endDate);
  const [dimensionsMap, setDimensionsMap] = useState<Record<string, boolean>>(
    tag.dimensionsMap ??
      DIMENSION_OPTIONS.reduce(
        (acc, opt) => ({ ...acc, [opt.value]: false }),
        {} as Record<string, boolean>
      )
  );
  const hasValidDimensions = Object.values(dimensionsMap).includes(true);

  // Placement type is the driver: all supported types are always offered, and
  // the vendor list (above, in `vendorOptions`) narrows to vendors that support
  // the chosen type (DEV-22505).
  const placementTypeOptions = useMemo(() => {
    const base = [
      { label: 'Audio VAST', value: 'Audio' },
      { label: 'Pixel', value: 'Pixel' },
      { label: 'Video VAST', value: 'VAST' },
      { label: 'Display', value: 'Display' }
    ];
    // Disable placement types incompatible with the flight's platform (e.g. a
    // Display flight can't run a Video VAST placement); Pixel is always allowed.
    return base.map(opt => {
      const disabledReason = getPlacementTypeDisabledReason(opt.value, tag.platform);
      return disabledReason ? { ...opt, disabled: true, simpleTooltip: disabledReason } : opt;
    });
  }, [tag.platform]);

  const instacartPixelOptions = useMemo(
    () =>
      companyInfo?.cid?.includes('instacart') &&
      placementType !== 'Pixel' &&
      adServer.toLowerCase() === 'flashtalking'
        ? INSTACART_PIXEL_OPTIONS
        : [],
    [adServer, companyInfo, placementType]
  );

  useEffect(() => {
    if (instacartPixelOptions.length === 0) {
      setInstacartPixel('');
    }
  }, [instacartPixelOptions]);

  useEffect(() => {
    if (vendorOptions.length === 1) {
      // Auto-select the only available vendor.
      setVendor(vendorOptions[0]);
    } else if (vendor && !vendorOptions.includes(vendor)) {
      // Clear a vendor that the newly chosen placement type no longer supports.
      setVendor('');
    }
  }, [vendor, vendorOptions]);

  const campaignMap = useMemo(() => {
    const map: Record<string, number> = {};
    for (const campaign of flashtalkingCampaigns) {
      map[campaign.campaign] = campaign.campaign_id;
    }
    return map;
  }, [flashtalkingCampaigns]);

  useEffect(() => {
    if (flashtalkingCampaigns.length === 1) {
      setCampaignID(flashtalkingCampaigns[0].campaign_id);
    }
  }, [flashtalkingCampaigns]);

  const [formFail, setFormFail] = useState(false);

  const constructFormData = useCallback((): TagFormData => {
    const formData: TagFormData = { adServer: 'Flashtalking' };
    if (hasDeviceTags(tag)) {
      formData.deviceMap = deviceMap;
    }
    if (tag.splitLength && placementType !== 'Display') {
      formData.lengthMap = lengthMap;
    }
    if (allowAdditionalTagParams) {
      formData.additionalParams = additionalParams;
    }
    formData.vendor = vendor;
    formData.adServer = adServer;
    formData.campaignID = campaignID;
    formData.startDate = startDate;
    formData.endDate = endDate;
    formData.placementType = placementType;
    formData.transcodeVendor = transcodeVendor;
    if (placementType === 'Display') {
      formData.dimensionsMap = dimensionsMap;
    }
    if (instacartPixel) {
      formData.extraTrackingPixels = [instacartPixel];
    }
    return formData;
  }, [
    additionalParams,
    adServer,
    allowAdditionalTagParams,
    campaignID,
    deviceMap,
    dimensionsMap,
    endDate,
    instacartPixel,
    lengthMap,
    placementType,
    startDate,
    tag,
    transcodeVendor,
    vendor
  ]);

  const updateTagWithFormData = useCallback((): TagData => {
    const formData = constructFormData();
    const updatedTag = { ...tag };
    if (formData.deviceMap) {
      updatedTag.deviceMap = formData.deviceMap;
    }
    if (formData.lengthMap) {
      updatedTag.lengthMap = formData.lengthMap;
    }
    if (formData.additionalParams) {
      updatedTag.additionalParams = formData.additionalParams;
    }
    updatedTag.vendor = formData.vendor;
    updatedTag.adServer = formData.adServer;
    updatedTag.campaignID = formData.campaignID;
    updatedTag.startDate = formData.startDate;
    updatedTag.endDate = formData.endDate;
    updatedTag.placementType = formData.placementType;
    updatedTag.transcodeVendor = formData.transcodeVendor;
    if (formData.dimensionsMap) {
      updatedTag.dimensionsMap = formData.dimensionsMap;
    }
    if (formData.extraTrackingPixels) {
      updatedTag.extraTrackingPixels = formData.extraTrackingPixels;
    }
    setTag(updatedTag);
    return updatedTag;
  }, [constructFormData, setTag, tag]);

  const attemptSubmit = useCallback(() => {
    // updateTagWithFormData also calls setTag, but that state update is async, so
    // we pass the freshly-built tag straight to onSubmit rather than relying on
    // the parent re-reading the (still-stale) tag prop.
    const updatedTag = updateTagWithFormData();
    const isValid =
      hasValidDevices &&
      (tag.splitLength && placementType !== 'Display' ? hasValidLengths : true) &&
      (placementType === 'Display' ? hasValidDimensions : true) &&
      vendor.length > 0 &&
      (adServer !== 'Flashtalking' || (campaignID && placementType));
    if (isValid) {
      onSubmit(updatedTag);
      setFormFail(false);
    } else {
      setFormFail(true);
    }
  }, [
    adServer,
    campaignID,
    hasValidDevices,
    hasValidDimensions,
    hasValidLengths,
    onSubmit,
    placementType,
    tag,
    updateTagWithFormData,
    vendor
  ]);

  return (
    <div className='tag-creation-form'>
      <Modal.Header closeButton>
        <Modal.Title>
          {additionalSourceTagCount > 0
            ? `Create Tags for ${additionalSourceTagCount + 1} Derived IDs`
            : `Create Tags for ${tag.derivedID}`}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <PlacementSummary network={tag.network} platform={tag.platform} />
        <div className='controls'>
          {hasDeviceTags(tag) && (
            <div className='selection-div'>
              <div className='selection-title'>Devices:</div>
              <SegmentedControlButton
                options={[DeviceNames.MOBILE, DeviceNames.CTV, DeviceNames.DESKTOP]}
                selectedOptions={Object.keys(deviceMap).filter(d => deviceMap[d])}
                isExclusiveSelect={false}
                onChange={device => setDeviceMap({ ...deviceMap, [device]: !deviceMap[device] })}
                size={Size.SM}
              />
            </div>
          )}
          {tag.splitLength && placementType !== 'Display' && (
            <div className='selection-div'>
              <div className='selection-title'>Lengths:</div>
              <SegmentedControlButton
                options={['6', '15', '30', '60', '120']}
                selectedOptions={Object.keys(lengthMap).filter(l => lengthMap[l])}
                isExclusiveSelect={false}
                onChange={length => setLengthMap({ ...lengthMap, [length]: !lengthMap[length] })}
                size={Size.SM}
              />
            </div>
          )}
          {allowAdditionalTagParams && (
            <div className='selection-div'>
              <div className='selection-title'>Additional Params (comma-separated):</div>
              <Form.Control type='text' onChange={e => splitAdditionalParams(e.target.value)} />
            </div>
          )}
          <div className='selection-div'>
            <div className='selection-title'>Ad Server:</div>
            <Dropdown
              overlayPlacement={Placement.RIGHT}
              options={[{ label: 'Flashtalking', value: 'Flashtalking' }]}
              value={adServer}
              onChange={e => {
                setAdServer(e);
                setVendor('');
              }}
              size={Size.SM}
            />
          </div>
          {campaignMap && adServer === 'Flashtalking' && (
            <div className='selection-div'>
              <div className='selection-title'>Campaign:</div>
              {flashtalkingCampaigns ? (
                <Dropdown
                  overlayPlacement={Placement.RIGHT}
                  onChange={e => setCampaignID(parseInt(e, 10))}
                  options={flashtalkingCampaigns
                    .filter(c => !c.is_managed_service)
                    .map(campaign => ({
                      label: `${campaign.campaign} (${campaign.campaign_id})`,
                      value: `${campaign.campaign_id}`
                    }))}
                  size={Size.SM}
                  value={`${campaignID || ''}`}
                />
              ) : (
                <a href='/adops/streaming' className={formFail ? 'invalid' : ''}>
                  Add campaigns for Flashtalking
                </a>
              )}
            </div>
          )}
          {campaignMap && adServer === 'Flashtalking' && (
            <div className='selection-div'>
              <div className='selection-title'>Start Date and End Date:</div>
              <DatePicker
                range={{
                  start: startDate ?? TODAY,
                  end:
                    endDate ??
                    Dfns.format(
                      DATE_FORMAT,
                      new Date(new Date().setFullYear(new Date().getFullYear() + 1))
                    )
                }}
                onChange={newDates => {
                  if (newDates.start && newDates.end) {
                    setStartDate(newDates.start);
                    setEndDate(newDates.end);
                  }
                }}
                isOutsideRange={() => false}
              />
            </div>
          )}
          {campaignMap && adServer === 'Flashtalking' && (
            <div className='selection-div'>
              <div className='selection-title'>Placement Type:</div>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                onChange={e => setPlacementType(e as 'Audio' | 'Pixel' | 'VAST' | 'Display')}
                options={placementTypeOptions}
                size={Size.SM}
                value={placementType}
              />
            </div>
          )}
          {campaignMap && placementType === 'Display' && (
            <div className='selection-div'>
              <div className='selection-title'>Dimensions:</div>
              <DimensionsPicker dimensionsMap={dimensionsMap} onChange={setDimensionsMap} />
            </div>
          )}
          <div className='selection-div'>
            <div className='selection-title'>Vendor:</div>
            {vendorOptions.length > 0 ? (
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                onChange={e => setVendor(e)}
                options={vendorOptions
                  .filter(option => option != null)
                  .map(v => ({ label: v, value: v }))}
                size={Size.SM}
                value={vendor}
              />
            ) : (
              <a href='/adops/streaming' className={formFail ? 'invalid' : ''}>
                Add vendors for {tag.network}
              </a>
            )}
          </div>
          {placementType !== 'Pixel' &&
            Object.values(FLASHTALKING_DSP_TO_VENDOR_MAP).includes(vendor) &&
            adServer === 'Flashtalking' && (
              <div className='selection-div'>
                <div className='selection-title'>Transcode Vendor (For Nexxen, other DSPs):</div>
                {tag.flashtalking_vendors ? (
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    onChange={e => setTranscodeVendor(e)}
                    options={tag.flashtalking_vendors.map(v => ({
                      label: v.pretty_name,
                      value: v.name
                    }))}
                    size={Size.SM}
                    value={transcodeVendor}
                  />
                ) : (
                  <a href='/adops/streaming' className={formFail ? 'invalid' : ''}>
                    Add vendors for {tag.network}
                  </a>
                )}
              </div>
            )}
          {instacartPixelOptions.length > 0 && (
            <div className='selection-div'>
              <div className='selection-title'>Instacart Additional Pixel:</div>
              <Dropdown
                overlayPlacement={Placement.RIGHT}
                onChange={value => setInstacartPixel(value)}
                options={instacartPixelOptions}
                size={Size.SM}
                value={instacartPixel}
              />
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={attemptSubmit}>Next</Button>
      </Modal.Footer>
    </div>
  );
};
